/**
 * Lecture temporelle du catalogue publicitaire.
 *
 * La régie lisait une liste plate triée par date de création : une campagne qui
 * démarre dans trois jours se trouvait entre deux campagnes closes, et rien ne
 * disait combien de temps il restait pour poser ce qui avait été vendu. Ce
 * module range les campagnes selon la seule chose qui compte au moment de les
 * regarder — où elles en sont par rapport à aujourd'hui — et compte les jours
 * qui séparent l'opérateur de la prochaine échéance.
 *
 * Tout est pur et prend `now` en argument : un composant qui lirait
 * `Date.now()` lui-même serait invérifiable, et un compteur « J-12 » calculé à
 * un instant tandis que le groupement l'est à un autre se contredisent au
 * passage de minuit.
 *
 * Les jours sont comptés en **journées civiles**, pas en tranches de 24 h : une
 * campagne qui démarre demain à 06:00 est à « J-1 » même si l'écart réel n'est
 * que de dix-huit heures, et les deux dimanches de changement d'heure ne
 * décalent pas le compte.
 */
import type { AdCampaign } from '../store/slices/adsSlice.js'

/**
 * Instant de référence. Les composants passent un `number` (`Date.now()` figé
 * par une prop), les tests une chaîne ISO : les trois formes sont acceptées
 * plutôt que d'obliger chaque appelant à convertir.
 */
export type TimeInput = string | number | Date

const PARIS_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Paris',
  year:     'numeric',
  month:    '2-digit',
  day:      '2-digit',
})

/** Jour civil d'antenne d'un instant, en nombre de jours depuis l'époque. */
function civilDayNumber(value: TimeInput): number {
  const instant = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(instant.getTime())) return Number.NaN
  const [year, month, day] = PARIS_DAY.format(instant).split('-').map(Number)
  return Math.round(Date.UTC(year, month - 1, day) / 86_400_000)
}

/**
 * Journées civiles entre `now` et `target`. Positif dans le futur, négatif dans
 * le passé, zéro le jour même.
 */
export function daysBetween(now: TimeInput, target: TimeInput): number {
  const from = civilDayNumber(now)
  const to   = civilDayNumber(target)
  return Number.isNaN(from) || Number.isNaN(to) ? 0 : to - from
}

/** Jours avant la mise en diffusion. Zéro ou négatif = déjà commencée. */
export function daysUntilStart(campaign: Pick<AdCampaign, 'startDate'>, now: TimeInput): number {
  return daysBetween(now, campaign.startDate)
}

/**
 * Jours restants avant la fin, dernier jour compris.
 *
 * La date de fin est éditoriale et inclusive (même convention que
 * `campaignPeriodScope` côté serveur) : une campagne qui se termine
 * aujourd'hui a encore un jour devant elle, pas zéro. Annoncer « 0 jour » sur
 * une campagne qui passe encore ce soir enverrait la régie chercher un
 * problème inexistant.
 */
export function daysLeft(campaign: Pick<AdCampaign, 'endDate'>, now: TimeInput): number {
  return Math.max(0, daysBetween(now, campaign.endDate) + 1)
}

/** Libellé du compte à rebours d'une campagne à venir. */
export function countdownLabel(days: number): string {
  if (days <= 0) return 'Aujourd’hui'
  return `J-${days}`
}

/** Libellé des jours restants d'une campagne en cours. */
export function remainingLabel(days: number): string {
  if (days <= 0) return 'Dernier jour'
  if (days === 1) return '1 j restant'
  return `${days} j restants`
}

/**
 * Les jours restants en deux parties, pour un affichage empilé.
 *
 * La liste écrit le chiffre en grand au-dessus de sa légende : sur une seule
 * ligne, « 12 jours restants » occupe la moitié d'une colonne de 280 px et
 * chasse les tranches horaires. Le dernier jour n'a pas de chiffre à montrer —
 * `value` est alors nul et seule la légende s'affiche.
 */
export interface RemainingParts {
  value:   number | null
  caption: string
}

export function remainingParts(days: number): RemainingParts {
  if (days <= 0) return { value: null, caption: 'Dernier jour' }
  return { value: days, caption: days === 1 ? 'jour restant' : 'jours restants' }
}

export type CampaignGroupKey = 'upcoming' | 'current' | 'other'

export const CAMPAIGN_GROUP_LABELS: Record<CampaignGroupKey, string> = {
  upcoming: 'À venir',
  current:  'En cours',
  other:    'Autres',
}

export interface CampaignGroups {
  /** Période pas encore commencée ; triées de la plus proche à la plus lointaine. */
  upcoming: AdCampaign[]
  /** Période en cours ; triées par échéance, la plus pressée d'abord. */
  current:  AdCampaign[]
  /**
   * Ni l'un ni l'autre : brouillons dont la période est passée, campagnes
   * annulées, et tout ce que la règle temporelle ne classe pas. Elles restent
   * visibles — les faire disparaître sans archivage serait une perte
   * silencieuse — mais en fin de liste.
   */
  other:    AdCampaign[]
}

export const CAMPAIGN_GROUP_ORDER: CampaignGroupKey[] = ['upcoming', 'current', 'other']

/**
 * Range les campagnes du catalogue courant en sections.
 *
 * Une campagne annulée n'est jamais « en cours », même si ses dates courent
 * encore : le statut prime sur le calendrier, sinon l'opérateur relirait à
 * l'antenne une campagne qu'il a lui-même arrêtée.
 */
export function groupCampaigns(campaigns: AdCampaign[], now: TimeInput): CampaignGroups {
  const groups: CampaignGroups = { upcoming: [], current: [], other: [] }

  for (const campaign of campaigns) {
    if (campaign.status === 'CANCELLED' || campaign.status === 'COMPLETED') {
      groups.other.push(campaign)
      continue
    }
    const untilStart = daysUntilStart(campaign, now)
    const untilEnd   = daysBetween(now, campaign.endDate)
    if (untilStart > 0)      groups.upcoming.push(campaign)
    else if (untilEnd >= 0)  groups.current.push(campaign)
    else                     groups.other.push(campaign)
  }

  groups.upcoming.sort((left, right) => daysUntilStart(left, now) - daysUntilStart(right, now)
    || left.name.localeCompare(right.name, 'fr'))
  groups.current.sort((left, right) => daysLeft(left, now) - daysLeft(right, now)
    || left.name.localeCompare(right.name, 'fr'))

  return groups
}

/**
 * Regroupement des archives par mois de clôture, le plus récent d'abord.
 *
 * C'est le seul repère dont dispose l'opérateur pour retrouver une campagne
 * close : ni son nom ni son annonceur ne disent quand elle s'est terminée.
 */
export interface ArchivedMonth {
  /** Clé `AAAA-MM`, stable et triable. */
  key:       string
  /** « Août 2026 », capitale initiale comprise. */
  label:     string
  campaigns: AdCampaign[]
}

const PARIS_MONTH = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  month:    'long',
  year:     'numeric',
})

export function groupArchivedByMonth(campaigns: AdCampaign[]): ArchivedMonth[] {
  const months = new Map<string, ArchivedMonth>()

  for (const campaign of campaigns) {
    // Une archive sans date retombe sur sa date de fin : mieux vaut un mois
    // approché qu'un tas « Sans date » que personne ne saurait lire.
    const reference = campaign.archivedAt ?? campaign.endDate
    const instant = new Date(reference)
    if (Number.isNaN(instant.getTime())) continue
    const [year, month] = PARIS_DAY.format(instant).split('-')
    const key = `${year}-${month}`
    const existing = months.get(key)
    if (existing) {
      existing.campaigns.push(campaign)
      continue
    }
    const raw = PARIS_MONTH.format(instant)
    months.set(key, {
      key,
      label:     raw.charAt(0).toUpperCase() + raw.slice(1),
      campaigns: [campaign],
    })
  }

  return [...months.values()]
    .sort((left, right) => right.key.localeCompare(left.key))
    .map(month => ({
      ...month,
      campaigns: [...month.campaigns].sort((left, right) =>
        new Date(right.archivedAt ?? right.endDate).getTime()
        - new Date(left.archivedAt ?? left.endDate).getTime()),
    }))
}

/**
 * Forme courte d'un créneau cible, pour la pastille d'une ligne de liste.
 *
 * Les créneaux sont enregistrés avec leur libellé complet — « Midi (12h-14h) »
 * — qui ne tient pas dans une colonne de 280 px. La tranche entre parenthèses
 * est la partie informative : c'est l'heure de passage qui se vend en
 * télévision locale, pas le nom de la tranche. Un créneau saisi sous une autre
 * forme (anciens codes `MATIN`, libellé libre) est rendu tel quel plutôt que
 * masqué.
 */
export function slotShortLabel(slot: string): string {
  const parenthesized = slot.match(/\(([^)]+)\)/)
  if (parenthesized) return parenthesized[1].trim()
  return slot.trim()
}

/** Les créneaux d'une campagne en forme courte ; vide = tous créneaux. */
export function slotShortLabels(campaign: Pick<AdCampaign, 'targetSlots'>): string[] {
  return campaign.targetSlots.map(slotShortLabel).filter(label => label.length > 0)
}
